import type { EventInfoData } from '@shared/utils/eventInfo';
import type { PageJson } from '../routes/CardCustomizer/CardCustomizer.types';
import type { TextLayerData } from '../routes/CardCustomizer/components/Layer.types';
import type { NumeralsTranslations } from './dateFormatting';
import { getOclock, getTimeOfDay, getTwoDigitsCardinalFromYear } from './dateFormatting';
import { fillPattern } from './fillPattern';
import { mapRecord } from './mapRecord';
import { toTitleCase } from './toTitleCase';
import type { PrimaryEventLocation } from '../routes/Dashboard/components/ProductDetailsPage/usePrimaryEventLocation';
import { utcToZonedTime } from 'date-fns-tz';
import type { PrivacyModeType } from '@graphql/generated-base';

export type Time = { milliseconds: number; timezone?: string };

const PASSWORD_FIELD_KEYS = ['password', 'Password', 'PASSWORD'] as const;
const PASSWORD_PATTERNS = new RegExp(`‹(?:${PASSWORD_FIELD_KEYS.join('|')})›`);

// Templates generated from the legacy theme system are already tagged.
// Each layer.name is mapped to one of values below.
// Rather than add a layer.pattern, we dynamically/virtually generate it.
const THEME_SYSTEM_LAYER_NAME_TO_PATTERN: Record<string, string> = {
  NAME1: '‹Firstname1›',
  NAME2: '‹Firstname2›',
  DATE: '‹WEEKDAY›, ‹MONTH› ‹D›, ‹YYYY›',
  VENUE: '‹VENUE›',
  TIME: '‹oclock› ‹timeofday›',
  LOCATION: '‹CityST›',
};

export function fillPageWithEventInfo(
  page: PageJson | undefined,
  eventInfo: EventInfoData | undefined,
  location: PrimaryEventLocation,
  tNumerals: NumeralsTranslations,
  privacyMode: PrivacyModeType,
  eventPassword: string | null,
): string[] {
  if (!page) return [];
  // Legacy templates have `card.back = {}` which does not conform to PageJson.
  if (!page.layers) return [];
  // `eventSchedule` undefined could mean either loading or does not exist.
  if (!eventInfo) return [];

  // prettier-ignore
  const date = utcToZonedTime(location.utcMilliseconds, location.timezone ?? '');

  const Firstname1 = eventInfo.ownerFirstName ?? '';
  const Firstname2 = eventInfo.fianceeFirstName ?? '';
  const h12 = date ? date.getHours() % 12 : undefined;

  const { Venue, Address, City, CityState, CityST } = location;

  const Alphabetic: Record<string, string> = {
    // Names
    F1: Firstname1 ? Firstname1[0] : '',
    F2: Firstname2 ? Firstname2[0] : '',
    Firstname1,
    Firstname2,
    Middlename1: '',
    Middlename2: '',
    Lastname1: eventInfo.ownerLastName ?? '',
    Lastname2: eventInfo.fianceeLastName ?? '',

    // Date
    Weekday: date?.toLocaleDateString('en-US', { weekday: 'long' }) ?? '',
    Day: tNumerals.ordinals[date?.getDate() ?? 0]?.() ?? '',
    Dth: tNumerals.numericOrdinals[date?.getDate() ?? 0]?.() ?? '',
    Month: date?.toLocaleDateString('en-US', { month: 'long' }) ?? '',
    Mon: date?.toLocaleDateString('en-US', { month: 'short' }) ?? '',
    TwoThousandYear: date ? `Two Thousand ${getTwoDigitsCardinalFromYear(date.getFullYear(), tNumerals)}` : '',
    Hour: tNumerals.cardinals[h12 ?? -1]?.() ?? '',
    Oclock: date ? getOclock(date, tNumerals) : '',
    Timeofday: date ? getTimeOfDay(date, tNumerals) : '',
    Am: date ? (date.getHours() < 12 ? 'am' : 'pm') : '',

    // Location
    Venue,
    Address,
    City,
    CityState,
    CityST,
  };

  const shouldShowPassword = privacyMode !== 'public';

  const YYYY = date?.toLocaleDateString('en-US', { year: 'numeric' }) ?? '';

  const fields: Record<string, string> = {
    // Alphabetics require case variants
    ...mapRecord(Alphabetic, str => toTitleCase(str)),
    ...mapRecord(Alphabetic, str => str.toUpperCase()),
    ...mapRecord(Alphabetic, str => str.toLowerCase()),

    // Password (derived from PASSWORD_FIELD_KEYS; excluded from analytics).
    // eventPassword === null means "not known yet" (e.g. a failed reveal): omit the keys so
    // fillPattern leaves the ‹Password› token literal instead of persisting a blank value.
    ...(eventPassword === null ? {} : Object.fromEntries(PASSWORD_FIELD_KEYS.map(k => [k, shouldShowPassword ? `${k}: ${eventPassword}` : '']))),

    // Numeric
    D: date?.toLocaleDateString('en-US', { day: 'numeric' }) ?? '',
    DD: date?.toLocaleDateString('en-US', { day: '2-digit' }) ?? '',
    M: date?.toLocaleDateString('en-US', { month: 'numeric' }) ?? '',
    MM: date?.toLocaleDateString('en-US', { month: '2-digit' }) ?? '',
    YY: YYYY.slice(-2),
    YYYY,
    h: (() => {
      if (h12 === undefined) return '';
      if (h12 === 0) return '12';
      return `${h12}`;
    })(),
    m: date?.getMinutes()?.toString() ?? '',
    mm: date?.getMinutes()?.toString().padStart(2, '0') ?? '',
  };

  const layers = page.layers.filter(layer => layer.type === 'text') as TextLayerData[];
  const filledPatterns: string[] = [];
  for (const layer of layers) {
    const pattern = layer.textData.pattern ?? THEME_SYSTEM_LAYER_NAME_TO_PATTERN[layer.name ?? ''];
    if (!pattern) continue;
    const filledPattern = fillPattern(pattern, fields);
    layer.textData.content = filledPattern;
    // We don't want to return passwords in the filled patterns to avoid accidentally leaking passwords to analytics.
    if (!PASSWORD_PATTERNS.test(pattern)) {
      filledPatterns.push(filledPattern);
    }
  }

  return filledPatterns;
}
